import React, { FC, ReactNode, useContext, useMemo } from "react";
import { ThemeContext } from "styled-components";
import { TenantContext } from "v3/context/tenant";
import { ShippingAddress } from "v3/services/graphql/generated";
import { BorderedTenantLogo } from "@pepdirect/v3/ui/BorderedTenantLogo";
import { PaymentImage } from "@pepdirect/v3/ui/PaymentImage";
import { CheckIconSvg } from "@pepdirect/v3/ui/icons";
import { colors } from "@pepdirect/v3/styles/variables";
import {
  StyledAddress,
  StyledChecked,
  StyledCheckedIcon,
  StyledContentContainer,
  StyledDefault,
  StyledImageWrapper,
  StyledOption,
  StyledRightAlignedContent,
  StyledTitle,
} from "./index.styled";

interface ImageProps {
  imageAlt?: string;
  imageSrc?: string;
  showTenantLogo?: boolean;
  isDisabled?: boolean;
}

interface SavedOptionProps extends ImageProps {
  checked?: boolean;
  children?: ReactNode | string;
  handleClick: () => void;
  imageAlt?: string;
  imageSrc?: string;
  isDefault?: boolean | null | undefined;
  isDisabled?: boolean;
  rightAlignedContent?: ReactNode | string;
  showTenantLogo?: boolean;
  title?: string | null | undefined;
  description?: string | null | undefined;
  distance?: string | null | undefined;
  checkpoint?: string | null | undefined;
  address?: ShippingAddress;
}

const PaymentImageHOC: FC<ImageProps> = ({
  imageAlt,
  imageSrc,
  showTenantLogo,
  isDisabled,
}) => {
  const { tenant } = useContext(TenantContext);
  if (!imageSrc) return null;

  return (
    <StyledImageWrapper>
      {showTenantLogo ? (
        <BorderedTenantLogo
          logoAlt={imageAlt}
          logoSrc={imageSrc}
          tenantId={tenant?.id}
          isDisabled={isDisabled}
        />
      ) : (
        <PaymentImage
          src={imageSrc}
          alt={imageAlt}
          height="48"
          width="70"
          isDisabled={isDisabled}
        />
      )}
    </StyledImageWrapper>
  );
};

export const SavedOption: FC<SavedOptionProps> = ({
  checked = false,
  children,
  handleClick,
  imageAlt,
  imageSrc = "",
  isDefault,
  isDisabled,
  rightAlignedContent,
  showTenantLogo,
  title,
  description,
  distance,
  checkpoint,
  address,
}) => {
  const imageProps = { imageAlt, showTenantLogo, imageSrc, isDisabled };
  const { line1, city, company } = address || {};
  const theme = useContext(ThemeContext);

  const formattedTitle = useMemo(() => {
    if (title && distance && checkpoint) {
      return `${title} | ${distance} | ${checkpoint}`;
    }
    return title;
  }, [title, distance, checkpoint]);

  const formattedAddress = useMemo(() => {
    if (!address) {
      return;
    }
    return `${line1}, ${city}, ${company}`;
  }, [address, line1, city, company]);

  return (
    <StyledOption
      checked={checked}
      isDisabled={isDisabled}
      onClick={!isDisabled ? handleClick : undefined}
    >
      <StyledChecked>
        {checked && (
          <StyledCheckedIcon>
            <CheckIconSvg
              color={isDisabled ? theme.color.disabled : colors.gray900}
              height="24"
              width="24"
            />
          </StyledCheckedIcon>
        )}
      </StyledChecked>
      <PaymentImageHOC {...imageProps} />
      <StyledContentContainer>
        {title && <StyledTitle>{formattedTitle}</StyledTitle>}
        {description && (
          <StyledRightAlignedContent>{description}</StyledRightAlignedContent>
        )}
        {address && <StyledAddress>{formattedAddress}</StyledAddress>}
        {isDefault && (
          <StyledDefault isDisabled={isDisabled}>Default</StyledDefault>
        )}
        {children}
      </StyledContentContainer>
      {rightAlignedContent && (
        <StyledRightAlignedContent $marginLeft={8}>
          {rightAlignedContent}
        </StyledRightAlignedContent>
      )}
    </StyledOption>
  );
};
